type CustomerSetupLinkPage<T> = {
  data: T[]
  meta: {
    current_page: number
    last_page: number
  }
}

type CustomerSetupLinksEligibility = {
  companyType: string
  customerSetupFormUrl?: string
  organisationSlug?: null | string
  verifiedAt?: Date | null
}

type TargetedCustomerSetupLinkEligibility = {
  canCreate: boolean
  customerSetupFormUrl?: string
  organisationSlug?: null | string
  verifiedAt?: Date | null
}

type UntargetedCustomerSetupLinkEligibility = Pick<
  TargetedCustomerSetupLinkEligibility,
  'canCreate' | 'customerSetupFormUrl' | 'organisationSlug'
>

export const buildCustomerSetupLink = (customerSetupFormUrl: string, token: string) => {
  const url = new URL(customerSetupFormUrl)
  url.searchParams.set('token', token)
  return url.toString()
}

const isKCharlesOrganisation = (organisationSlug?: null | string) =>
  organisationSlug === 'k-charles' || organisationSlug === 'kcharles'

const isCustomerOrBroker = (companyType: string) =>
  ['broker', 'brokers', 'customer', 'customers', 'shipper', 'shippers'].includes(companyType)

export const isCustomerSetupLinksEligible = ({
  companyType,
  customerSetupFormUrl,
  organisationSlug,
  verifiedAt,
}: CustomerSetupLinksEligibility) =>
  Boolean(customerSetupFormUrl) &&
  !verifiedAt &&
  isCustomerOrBroker(companyType) &&
  isKCharlesOrganisation(organisationSlug)

export const isTargetedCustomerSetupLinkEligible = ({
  canCreate,
  customerSetupFormUrl,
  organisationSlug,
  verifiedAt,
}: TargetedCustomerSetupLinkEligibility) =>
  canCreate &&
  Boolean(customerSetupFormUrl) &&
  !verifiedAt &&
  isKCharlesOrganisation(organisationSlug)

export const isUntargetedCustomerSetupLinkEligible = ({
  canCreate,
  customerSetupFormUrl,
  organisationSlug,
}: UntargetedCustomerSetupLinkEligibility) =>
  canCreate &&
  Boolean(customerSetupFormUrl) &&
  (organisationSlug === undefined ||
    organisationSlug === 'cargoking' ||
    isKCharlesOrganisation(organisationSlug))

type SetupFormSourcePresentation = {
  deleted: boolean
  detail: string
  label: 'Public form' | 'Setup link'
  vat?: string
}

type SetupFormSourceTargetCustomer = {
  deleted: boolean
  title: string
  vat?: null | string
}

export const getSetupFormSourcePresentation = ({
  setupFormTokenId,
  targetCompany,
}: {
  setupFormTokenId?: null | number
  targetCompany?: null | SetupFormSourceTargetCustomer
}): SetupFormSourcePresentation => {
  if (setupFormTokenId == null) {
    return {
      deleted: false,
      detail: 'Submitted directly',
      label: 'Public form' as const,
    }
  }

  if (!targetCompany) {
    return {
      deleted: false,
      detail: 'New Customer',
      label: 'Setup link' as const,
    }
  }

  const vat = targetCompany.vat?.trim()

  return {
    deleted: targetCompany.deleted,
    detail: targetCompany.title,
    label: 'Setup link' as const,
    ...(vat ? { vat } : {}),
  }
}

export const fetchAllCustomerSetupLinkPages = async <T>(
  fetchPage: (_page: number) => Promise<CustomerSetupLinkPage<T>>,
) => {
  const firstPage = await fetchPage(1)
  const remainingPages = await Promise.all(
    Array.from({ length: firstPage.meta.last_page - firstPage.meta.current_page }, (_, index) =>
      fetchPage(firstPage.meta.current_page + index + 1),
    ),
  )

  return [firstPage, ...remainingPages].flatMap(page => page.data)
}

export const mergeCustomerSetupLinksNewestFirst = <T extends { created_at: Date; id: number }>(
  links: T[],
  localLink?: T,
) => {
  const uniqueLinks = new Map<number, T>()

  for (const link of links) uniqueLinks.set(link.id, link)
  if (localLink) uniqueLinks.set(localLink.id, localLink)

  const sortedLinks = [...uniqueLinks.values()].toSorted(
    (left, right) => right.created_at.getTime() - left.created_at.getTime(),
  )

  if (!localLink) return sortedLinks

  return [localLink, ...sortedLinks.filter(link => link.id !== localLink.id)]
}
